// The beta front door at autheniq.ai: one access code, one SCREEN.
//
// Everything fits a single viewport with no scrolling: the left half sells
// (headline, one-liner, three chips), the right half acts (live test on top,
// install options below). When a live test starts, the trust list replaces
// the right panel — still the same screen. On phones the QR codes become
// direct install buttons: nobody scans a QR with the phone it is shown on.
//
// The gate is a velvet rope, not a vault — the code is hash-compared
// client-side for looks, and re-presented to the API where it is enforced
// for real (the live test deploys bots that cost money).
import React, { useEffect, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import LiveTest from './LiveTest';
import './Landing.css';

// sha256("PROVEIT") — change the code by replacing this hash.
const GATE_HASH = '8f9711352c1f1aa8e326e15dcdc884a45c15bbc7a9e3ea5ff059aaad82df87f1';
const GATE_KEY = 'autheniq.beta.gate';
export const CODE_KEY = 'autheniq.beta.code';

async function sha256Hex(text) {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

export function useGate() {
  const [open, setOpen] = useState(() => {
    try { return localStorage.getItem(GATE_KEY) === GATE_HASH; } catch { return false; }
  });
  const tryCode = async (code) => {
    const hash = await sha256Hex(code.trim().toUpperCase());
    if (hash === GATE_HASH) {
      try {
        localStorage.setItem(GATE_KEY, hash);
        // The live test re-presents the code to the API, which enforces it
        // server-side — the one place the rope is real.
        localStorage.setItem(CODE_KEY, code.trim().toUpperCase());
      } catch { /* private mode */ }
      setOpen(true);
      return true;
    }
    return false;
  };
  return { open, tryCode };
}

export function Gate({ onOpen }) {
  const [code, setCode] = useState('');
  const [shake, setShake] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!(await onOpen(code))) {
      setShake(true);
      setCode('');
      setTimeout(() => setShake(false), 500);
    }
  };

  return (
    <div className="gate">
      <div className={`gate-card gate-wide ${shake ? 'shake' : ''}`}>
        <img className="brand-logo" src="/brand-logo.png" alt="AuthenIQ" />
        <p className="gate-sub">Private beta</p>
        <form onSubmit={submit}>
          <input
            autoFocus
            type="password"
            value={code}
            placeholder="Access code"
            onChange={(e) => setCode(e.target.value)}
            aria-label="Access code"
          />
          <button type="submit">Enter</button>
        </form>
        <div className="gate-divider" />
        <p className="deck-label">Pitch deck</p>
        <a className="deck-button" href="/AuthenIQ_Pitch.html" download="AuthenIQ_Pitch.html">
          Download
        </a>
      </div>
    </div>
  );
}

const GET_IOS = 'https://www.autheniq.ai/get/ios';
const GET_ANDROID = 'https://www.autheniq.ai/get/android';
const GET_HALO =
  'https://chromewebstore.google.com/detail/autheniq-halo/fcnakoophghehlnjbbkpefclhaifoked';

const CHIPS = [
  { mark: '⚠', text: 'Authentication ends at login. Deepfakes join after.' },
  { mark: '∞', text: 'AuthenIQ keeps verifying, every minute of the session' },
  { mark: '✓', text: 'Verdicts the whole room can see, in the open' },
];

export default function Landing() {
  const { open, tryCode } = useGate();

  useEffect(() => { document.title = 'AuthenIQ'; }, []);

  if (!open) return <Gate onOpen={tryCode} />;

  return (
    <div className="screen">
      <div className="sell">
        <img className="brand-logo" src="/brand-logo.png" alt="AuthenIQ" />
        <h1>
          Anyone can be faked.
          <br />
          <span className="grad">You can be verified.</span>
        </h1>
        <p className="lede">
          The continuous human identity layer for virtual environments, live today on Zoom,
          Meet, and Teams. Not a bot. Not a deepfake. Not a borrowed login.
        </p>
        <ul className="chips">
          {CHIPS.map((c) => (
            <li key={c.mark}>
              <span className="chip-mark">{c.mark}</span>
              {c.text}
            </li>
          ))}
        </ul>
        <span className="foot">© {new Date().getFullYear()} AuthenIQ · Private beta</span>
      </div>

      <div className="act">
        <LiveTest />
        <div className="install">
          <div className="qrs desktop-only">
            <a className="qr" href={GET_IOS}>
              <QRCodeSVG value={GET_IOS} size={116} bgColor="transparent" fgColor="#eceafb" />
              <span> iPhone</span>
            </a>
            <a className="qr" href={GET_ANDROID}>
              <QRCodeSVG value={GET_ANDROID} size={116} bgColor="transparent" fgColor="#eceafb" />
              <span>🤖 Android</span>
            </a>
          </div>
          <div className="store-buttons mobile-only">
            {/* /get/ios opens the web app until a public TestFlight link exists */}
            <a className="store" href={GET_IOS}> Open on iPhone</a>
            <a className="store" href={GET_ANDROID}>🤖 Install for Android</a>
          </div>
          <a className="weblink" href="/app">No install? Open the web app →</a>
          <a className="weblink" href={GET_HALO}>
            Show it on your camera: Halo for Chrome →
          </a>
        </div>
      </div>
    </div>
  );
}
